import { ImpactLanguage } from '@upright/common/model/input/types';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { getOfficialTime, getVersionAndRelease } from '../../../../../../../../untidy/redux/selectors';
import { BouColor } from '../../../../../../../generic/global/bou-colors';
import { useImpactChartContext, useImpactChartFontSizes } from '../../context';
import { leena2VersionMarkerSuffix } from '@upright/ui/common/upright/model/output/leena2-version-marker';

import finnishLocale from 'date-fns/locale/fi';
import swedishLocale from 'date-fns/locale/sv';

interface Props {
  version?: string;
  release?: string;
  className?: string;
  language?: ImpactLanguage;
}

export function ModelReleaseText(props: Props) {
  const { staticMode } = useImpactChartContext();
  const { modelVersionText: fontSize } = useImpactChartFontSizes();
  const time = useSelector(getOfficialTime);
  const { version: defaultVersion, release: defaultRelease } = useSelector(getVersionAndRelease);
  if (defaultVersion === 'unknown' || defaultRelease === 'unknown') {
    return <></>;
  }
  const version = props.version || defaultVersion;
  const release = props.release || defaultRelease;
  const timeString = getLocalizedTimeString(time, staticMode, props.language);
  return (
    <Root className={props.className} style={{ fontSize }}>
      {getLocalizedVersionString(release, version, props.language)}
      <br />
      {timeString}
    </Root>
  );
}

const Root = styled.div`
  color: ${BouColor.GREY_B3B3B3};
  b {
    font-weight: 600;
  }
`;

function getLocalizedVersionString(
  release: string | null,
  version: string,
  language: ImpactLanguage = ImpactLanguage.en,
): JSX.Element {
  if (release === null) {
    switch (language) {
      case ImpactLanguage.en:
        return (
          <>
            Upright model version <b>{version}</b>
            {leena2VersionMarkerSuffix(version)}
          </>
        );
      case ImpactLanguage.fi:
        return (
          <>
            Upright-malliversio <b>{version}</b>
            {leena2VersionMarkerSuffix(version)}
          </>
        );
      case ImpactLanguage.sv:
        return (
          <>
            Upright modellversion <b>{version}</b>
            {leena2VersionMarkerSuffix(version)}
          </>
        );
    }
  }
  switch (language) {
    case ImpactLanguage.en:
      return (
        <>
          Upright model release <b>{release}</b>
        </>
      );
    case ImpactLanguage.fi:
      return (
        <>
          Upright-mallijulkaisu <b>{release}</b>
        </>
      );
    case ImpactLanguage.sv:
      return (
        <>
          Upright modellversion <b>{release}</b>
        </>
      );
  }
}

function getLocalizedTimeString(time: Date, staticMode: boolean, language: ImpactLanguage = ImpactLanguage.en): string {
  const timeFormat = `HH:mm${staticMode ? ' zzz' : ''}`;
  switch (language) {
    case ImpactLanguage.en:
      return `on ${format(time, 'do MMM. yyyy')} at ${simplifyGMT(format(time, timeFormat))}`;
    case ImpactLanguage.fi:
      return `${format(time, 'd.M.yyyy', { locale: finnishLocale })} klo ${simplifyGMT(
        format(time, timeFormat, { locale: finnishLocale }),
      )}`;
    case ImpactLanguage.sv:
      return `${format(time, 'd.M.yyyy', { locale: swedishLocale })} klo ${simplifyGMT(
        format(time, timeFormat, { locale: swedishLocale }),
      )}`;
  }
}

function simplifyGMT(timeString: string) {
  return timeString.replace('GMT+0', 'GMT');
}
